import { Box, ButtonBase, Typography } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Skeleton } from "../../shared";
import {
  getHistory,
  HistoryEnvelope,
  HistoryOrder,
} from "../../../services/orders/history";
import {
  getShortStatusLabel,
  getTypeLabel,
  STATUS_META,
  TYPE_META,
} from "../history/helpers";
import i18nInstance from "../../../i18n";

interface WalletTransactionsSectionProps {
  isLoading?: boolean;
}

/** Короткий человеко-читаемый статус для карточки «Последние транзакции». */
function shortStatus(o: HistoryOrder, t: TFunction): { text: string; color: string } {
  const meta = STATUS_META[o.status];
  return { text: getShortStatusLabel(o.status, t), color: meta.color };
}

/** Форматирует ISO-дату в короткий вид (локаль зависит от языка). */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const locale = (i18nInstance.resolvedLanguage || i18nInstance.language || "en").slice(0, 2);
  return d.toLocaleString(locale === "ru" ? "ru-RU" : "en-GB");
}

function formatAmount(v: string | null | undefined, digits = 2): string {
  if (!v) return "0";
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("ru-RU", {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

/** Заголовок строки — учитывает виртуальный тип SBP_PAYOUT. */
function getTitle(o: HistoryOrder, t: TFunction): string {
  if (o.type === "SBP_PAYOUT") return getTypeLabel(o.type, t);
  if (o.type === "CARD_ISSUE") return getTypeLabel(o.type, t);
  if (o.type === "CARD_TOPUP") return getTypeLabel(o.type, t);
  if (o.type === "DEPOSIT") {
    return `${getTypeLabel("DEPOSIT", t)} ${o.buy_currency ?? ""}`.trim();
  }
  if (o.type === "WITHDRAW") {
    return `${getTypeLabel("WITHDRAW", t)} ${o.sell_currency ?? ""}`.trim();
  }
  // EXCHANGE
  const isIncome = o.buy_currency && o.buy_currency !== "RUB";
  const c = isIncome ? o.buy_currency : o.sell_currency;
  const verb = isIncome ? t("history:types.buy") : t("history:types.sell");
  return `${verb} ${c ?? ""}`.trim();
}
// Помечаем как использованный, чтобы TS не ругался.
void TYPE_META;

/** Основная сумма справа + цвет. */
function getAmount(o: HistoryOrder): { text: string; color: string } {
  if (o.type === "SBP_PAYOUT") {
    // Показываем в рублях (сумма чека) — это то, что «уходит» получателю.
    return { text: `−${formatAmount(o.buy_amount, 0)} ₽`, color: "text.primary" };
  }
  if (o.type === "DEPOSIT") {
    return {
      text: `+${formatAmount(o.buy_amount ?? o.sell_amount)} ${o.buy_currency ?? ""}`.trim(),
      color: "#00CA48",
    };
  }
  if (o.type === "WITHDRAW") {
    return {
      text: `−${formatAmount(o.sell_amount)} ${o.sell_currency ?? ""}`.trim(),
      color: "text.primary",
    };
  }
  if (o.type === "CARD_ISSUE" || o.type === "CARD_TOPUP") {
    return {
      text: `−${formatAmount(o.sell_amount)} ${o.sell_currency ?? ""}`.trim(),
      color: "text.primary",
    };
  }
  // EXCHANGE — приход по buy_currency (для покупки крипты за RUB это плюсовая USDT)
  const isIncome = o.buy_currency && o.buy_currency !== "RUB";
  return {
    text: `${isIncome ? "+" : "−"}${formatAmount(o.buy_amount)} ${o.buy_currency ?? ""}`.trim(),
    color: isIncome ? "#00CA48" : "text.primary",
  };
}

function TransactionRow({ order }: { order: HistoryOrder }) {
  const { t } = useTranslation(["history", "common"]);
  const status = shortStatus(order, t);
  const title = getTitle(order, t);
  const amount = getAmount(order);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "14px 16px",
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "text.primary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginTop: "3px",
          }}
        >
          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              bgcolor: status.color,
            }}
          />
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            {status.text} · {formatDate(order.created_at)}
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: amount.color,
          flexShrink: 0,
        }}
      >
        {amount.text}
      </Typography>
    </Box>
  );
}

export function WalletTransactionsSection({
  isLoading: parentLoading,
}: WalletTransactionsSectionProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("history");
  const { data, isLoading: queryLoading } = useQuery<HistoryEnvelope>({
    queryFn: () => getHistory({ page: 1, pageSize: 20 }),
    queryKey: ["wallet-history"],
  });
  const isLoading = parentLoading || queryLoading;
  const recent = (data?.items ?? []).slice(0, 3);
  const hasTransactions = recent.length > 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
          paddingInline: "4px",
        }}
      >
        {isLoading ? (
          <>
            <Skeleton
              variant="rounded"
              width={180}
              height={22}
              sx={{ borderRadius: "8px" }}
            />
            <Skeleton
              variant="rounded"
              width={40}
              height={20}
              sx={{ borderRadius: "8px" }}
            />
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
              {t("sections.recent_title")}
            </Typography>
            <ButtonBase
              onClick={() => navigate("/history")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "2px",
                color: "text.secondary",
                fontSize: "14px",
                fontWeight: 500,
                padding: "4px 6px",
                borderRadius: "8px",
              }}
            >
              {t("sections.see_all")}
              <ChevronRight sx={{ fontSize: 18, color: "text.secondary", opacity: 0.7 }} />
            </ButtonBase>
          </>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "grid", gap: "10px" }}>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={64}
              sx={{ borderRadius: "18px" }}
            />
          ))}
        </Box>
      ) : hasTransactions ? (
        <Box
          sx={{
            borderRadius: "24px",
            bgcolor: "secondary.main",
            overflow: "hidden",
            "& > *:not(:last-child)": {
              borderBottom: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          {recent.map((order) => (
            <TransactionRow key={order.id} order={order} />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: "24px",
            bgcolor: "secondary.main",
            padding: "40px 16px",
            minHeight: "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            {t("sections.no_transactions")}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
